import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LogOut, Download, ArrowUpRight, Trophy, Sparkles } from "lucide-react";
import { useMember } from "@/contexts/MemberAuthContext";
import { MissionsWidget, ReferralWidget, RewardsPanel } from "@/components/GrowthWidgets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { member, loading, logout, refresh, memberApi } = useMember();
  const [badges, setBadges] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ display_name: "", bio: "" });
  const [params] = useSearchParams();
  const subscribed = params.get("subscribed");

  useEffect(() => {
    axios.get(`${API}/badges`).then((r) => setBadges(r.data || []));
    axios.get(`${API}/tiers`).then((r) => setTiers(r.data || []));
    if (subscribed) refresh();
  }, [subscribed, refresh]);

  useEffect(() => {
    if (member) setForm({ display_name: member.display_name || "", bio: member.bio || "" });
  }, [member]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!member) return <Navigate to="/login" replace state={{ from: "/account" }} />;

  const currentTier = tiers.find((t) => t.slug === member.tier_slug) || {};
  const nextTier = tiers.filter((t) => (t.level || 0) > (currentTier.level || 0)).sort((a, b) => a.level - b.level)[0];
  const progress = nextTier
    ? Math.min(100, Math.round(((member.points || 0) / (nextTier.points_threshold || 1)) * 100))
    : 100;

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await memberApi.post("/member/portal");
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Unable to open portal");
    } finally { setPortalBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await memberApi.put("/member/me", form);
      await refresh();
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] mb-2">MY ACCOUNT</p>
            <h1 className="font-heading text-3xl md:text-4xl text-white tracking-tight" data-testid="account-display-name">
              {member.display_name || member.username}
            </h1>
            <p className="text-white/40 text-xs mt-1">{member.email}</p>
          </div>
          <button onClick={logout} data-testid="member-logout" className="flex items-center gap-2 text-xs text-white/40 hover:text-red-400 uppercase tracking-widest">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        {subscribed && (
          <div className="mb-6 p-4 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded text-[#D4AF37] text-sm" data-testid="subscribed-banner">
            🎉 Subscription active. Welcome to the {member.tier_name || currentTier.name}.
          </div>
        )}

        {/* Tier + Points */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-2 bg-[#0A0A0A] border border-white/5 rounded-lg p-6" data-testid="tier-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">Current Tier</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-3xl" style={{ color: currentTier.color || "#D4AF37" }}>{currentTier.badge_emoji || "◇"}</span>
                  <h3 className="font-heading text-2xl text-white tracking-wider">{member.tier_name || "Explorer"}</h3>
                </div>
              </div>
              {member.stripe_subscription_id ? (
                <button
                  onClick={openPortal}
                  disabled={portalBusy}
                  data-testid="stripe-portal-btn"
                  className="text-xs px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors uppercase tracking-widest"
                >
                  {portalBusy ? "..." : "Manage Subscription"}
                </button>
              ) : (
                <Link to="/membership" data-testid="upgrade-link" className="text-xs px-4 py-2 bg-[#D4AF37] text-black hover:bg-white transition-colors uppercase tracking-widest">
                  Upgrade
                </Link>
              )}
            </div>

            {nextTier && (
              <div>
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-white/40 mb-2">
                  <span>Progress to {nextTier.name}</span>
                  <span data-testid="points-progress">{member.points || 0} / {nextTier.points_threshold} pts</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#8B0000]" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>

          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6" data-testid="points-card">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Lifetime Points</p>
            <div className="flex items-end gap-2">
              <Trophy className="w-8 h-8 text-[#D4AF37]" />
              <span className="text-4xl text-white font-bold" data-testid="points-total">{member.points || 0}</span>
            </div>
            <p className="text-white/30 text-[11px] mt-2">Earn points every time you purchase, stream, share, or refer a friend.</p>
          </div>
        </div>

        {/* Benefits */}
        {(currentTier.benefits || []).length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Your Benefits</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {currentTier.benefits.map((b, i) => (
                <div key={i} className="flex gap-2 text-sm text-white/70"><Sparkles className="w-3.5 h-3.5 text-[#D4AF37] mt-1 flex-shrink-0" /> {b}</div>
              ))}
            </div>
          </div>
        )}

        {/* Growth Engine widgets */}
        <MissionsWidget />
        <RewardsPanel />
        <ReferralWidget />

        {/* Badges */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4">Badges</p>
          <div className="flex flex-wrap gap-3">
            {badges.map((b) => {
              const earned = (member.badges || []).includes(b.slug);
              return (
                <div
                  key={b.slug}
                  data-testid={`badge-${b.slug}`}
                  className={`px-4 py-3 rounded border flex flex-col items-center min-w-[100px] transition-all ${
                    earned ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/5 opacity-40"
                  }`}
                  title={b.criterion}
                >
                  <span className="text-2xl mb-1">{b.icon}</span>
                  <span className="text-[10px] text-white/60 uppercase tracking-wider">{b.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Orders */}
        <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Purchases · {member.orders_count || 0}</p>
            <Link to="/my-downloads" data-testid="my-downloads-link" className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white flex items-center gap-1">
              <Download className="w-3 h-3" /> Downloads
            </Link>
          </div>
          {(member.orders || []).length === 0 ? (
            <p className="text-white/30 text-xs">No orders yet. <Link to="/music" className="text-[#D4AF37] hover:text-white">Browse music</Link></p>
          ) : (
            <div className="space-y-2">
              {(member.orders || []).slice(0, 8).map((o) => (
                <div key={o.id} className="flex justify-between items-center py-2 border-b border-white/5 text-sm">
                  <div>
                    <div className="text-white">{o.product_title || "Order"}</div>
                    <div className="text-white/30 text-[10px]">{o.paid_at ? new Date(o.paid_at).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="text-[#D4AF37] font-medium">${(o.amount || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-right mt-3 text-xs text-white/40">
            Lifetime spend: <span className="text-[#D4AF37] font-medium">${(member.total_spent || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Profile edit */}
        <form onSubmit={saveProfile} className="bg-[#0A0A0A] border border-white/5 rounded-lg p-6 mb-8 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Profile</p>
          <input
            type="text" placeholder="Display Name" data-testid="profile-display-name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
          />
          <textarea
            placeholder="Bio (max 240)" data-testid="profile-bio" maxLength={240} rows={2}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
          />
          <button
            disabled={saving}
            data-testid="save-profile-btn"
            className="text-xs px-6 py-2 border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] uppercase tracking-widest"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </form>

        {!member.stripe_subscription_id && (
          <Link
            to="/membership"
            data-testid="see-membership-link"
            className="flex items-center justify-center gap-2 border border-[#D4AF37]/30 py-4 text-[#D4AF37] hover:bg-[#D4AF37]/5 uppercase text-xs tracking-widest transition-colors"
          >
            Explore Premium Tiers <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}
